import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRightIcon, CopyIcon, LoaderIcon, MessageCircleIcon } from 'lucide-react';
import { useLaunch } from '../contexts/LaunchContext';
import { StoreBadges } from './StoreBadges';
import { SITE_URL, SUPPORT_EMAIL, WEB_JOIN_URL } from '../data/launch';
const WAITLIST_URL = 'https://script.google.com/macros/s/AKfycbwh0J9Vkq6zT5v5-Pe-5GZczrP-7RSoI2ANR30Epc1TUMBa6CtWBK-Hu7W5Kn1EZVlOfw/exec';
const WHATSAPP_SHARE_URL = `https://wa.me/?text=${encodeURIComponent('KittyPot makes it simple to track shared expenses and settle up. Join the early-access waitlist: https://www.kittypot.com')}`;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export function FinalCTA() {
  const {
    isPublicLaunch,
    googlePlayUrl
  } = useLaunch();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLinkCopied, setIsLinkCopied] = useState(false);
  const hasSubmittedRef = useRef(false);
  const isInitialIframeLoadRef = useRef(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resetStatusRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (resetStatusRef.current) clearTimeout(resetStatusRef.current);
    };
  }, []);
  const clearTimers = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (resetStatusRef.current) clearTimeout(resetStatusRef.current);
  };
  const showError = (message: string) => {
    clearTimers();
    hasSubmittedRef.current = false;
    setErrorMessage(message);
    setStatus('error');
    resetStatusRef.current = setTimeout(() => setStatus('idle'), 4000);
  };
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (status === 'loading') {
      e.preventDefault();
      return;
    }
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName || !trimmedEmail || !EMAIL_PATTERN.test(trimmedEmail)) {
      e.preventDefault();
      showError(!trimmedName ? 'Please enter your full name.' : 'Please enter a valid email address.');
      return;
    }
    const form = e.currentTarget;
    const nameInput = form.elements.namedItem('name') as HTMLInputElement;
    const emailInput = form.elements.namedItem('email') as HTMLInputElement;
    nameInput.value = trimmedName;
    emailInput.value = trimmedEmail;
    setName(trimmedName);
    setEmail(trimmedEmail);
    clearTimers();
    setErrorMessage('');
    hasSubmittedRef.current = true;
    setStatus('loading');
    timeoutRef.current = setTimeout(() => {
      showError('We couldn’t submit your details. Please try again.');
    }, 8000);
  };
  const handleIframeLoad = () => {
    if (isInitialIframeLoadRef.current) {
      isInitialIframeLoadRef.current = false;
      return;
    }
    if (!hasSubmittedRef.current) return;
    clearTimers();
    hasSubmittedRef.current = false;
    setStatus('success');
    setName('');
    setEmail('');
    setMarketingOptIn(false);
    resetStatusRef.current = setTimeout(() => setStatus('idle'), 5000);
  };
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(SITE_URL);
      setIsLinkCopied(true);
      window.setTimeout(() => setIsLinkCopied(false), 2000);
    } catch {
      window.prompt('Copy this link:', SITE_URL);
    }
  };
  return <section id="get-started" className={`relative py-24 lg:py-32 overflow-hidden ${isPublicLaunch ? 'bg-kitty-yellow' : 'bg-gray-950'}`} data-id="element-69" data-mp="3bb9mo8id3oo9">
      {!isPublicLaunch && <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(251,191,36,0.08),transparent_60%)]" data-id="element-70" data-mp="14j8un8vadk39" />}

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-71" data-mp="35njd8hwerfkp">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.5
      }} data-id="element-72" data-mp="35l6zkgxonrdg">
          <div className="bg-white p-2 rounded-2xl w-24 h-24 mx-auto mb-6 flex items-center justify-center shadow-soft" data-id="element-73" data-mp="2rojola8wb0oj">
            <img src="https://cdn.magicpatterns.com/uploads/vpaWAD2nLEfW1hoAd36jz7/LPot_only.png" alt="" className="w-16 h-16 object-contain" data-id="element-74" data-mp="2mpgvmo66wkwx" />
          </div>

          {isPublicLaunch ? <>
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-kitty-dark leading-[1.05] tracking-tight mb-5" data-id="element-75" data-mp="2cznkfo9ym7t7">
                Going together? Put it in the pot.
              </h2>
              <p className="text-lg lg:text-xl font-medium text-kitty-dark/80 mb-8 max-w-lg mx-auto" data-id="element-76" data-mp="23ww5vflsevxc">
                Create your first kitty and let KittyPot keep track of the rest.
              </p>

              {googlePlayUrl && <div className="flex justify-center mb-6" data-id="element-77" data-mp="egsigzijuly0">
                  
                </div>}

              <div className="flex justify-center mb-8" data-id="element-78" data-mp="jebwpzf8yypy">
                <StoreBadges tone="light" align="center" data-id="element-79" data-mp="nhib5ucijw7i" />
              </div>

              <p className="text-base font-semibold text-kitty-dark/80" data-id="element-80" data-mp="sgl62lys4m1r">
                Already invited to a kitty?{' '}
                <a href={WEB_JOIN_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4 hover:text-kitty-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-dark rounded" data-id="element-81" data-mp="3rwrksdwgypp8">
                  Join with your link or code
                </a>
                .
              </p>
            </> : <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white leading-tight tracking-tight mb-5" data-id="element-82" data-mp="9jjx6q23u1sj">
              Track together.{' '}
              <span className="text-kitty-yellow" data-id="element-83" data-mp="3d6slu8x3ba8p">Settle clearly.</span>
            </h2>}

          {isPublicLaunch ? null : <>
              <p className="text-lg lg:text-xl text-gray-400 mb-10 max-w-lg mx-auto" data-id="element-84" data-mp="kbao5ab1woyi">
                Join the KittyPot waitlist to hear when the app launches and to
                get early access.
              </p>

              <iframe name="waitlist_target" title="Waitlist submission" src="about:blank" onLoad={handleIframeLoad} className="hidden" data-id="element-85" data-mp="t4465fhvu2g2" />

              {status === 'success' ? <motion.div initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} className="max-w-lg mx-auto mb-6 bg-green-500/10 border border-green-500/20 rounded-2xl px-6 py-4 flex items-start justify-center gap-3" role="status" data-id="element-86" data-mp="1cy2wq6v7omk">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" data-id="element-87" data-mp="5e9s4ultu7u8" />
                  <div className="text-left flex-1" data-id="element-88" data-mp="1lsrvpn9nzdzf">
                    <p className="text-green-300 font-semibold" data-id="element-89" data-mp="1lve3no0gvvi6">
                      You're on the waitlist 🎉
                    </p>
                    <p className="text-green-400/60 text-sm mt-1" data-id="element-90" data-mp="1paz1yol4ugh8">
                      We'll email you when KittyPot launches.
                    </p>
                    <div className="mt-4 border-t border-green-400/15 pt-3" data-id="element-91" data-mp="1tazhkcjywxpv">
                      <p className="text-sm font-medium text-green-200" data-id="element-92" data-mp="1fevsexnybfk9">
                        Know someone who’d love this? Share KittyPot
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2" data-id="element-93" data-mp="281qa5mtdw0u1">
                        <a href={WHATSAPP_SHARE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-green-400/10 px-3 py-1.5 text-xs font-semibold text-green-200 transition-colors hover:bg-green-400/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow" data-id="element-94" data-mp="284ck1t3qysff">
                          <MessageCircleIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-95" data-mp="2r0kpawwrf3aq" />
                          WhatsApp
                        </a>
                        <button type="button" onClick={handleCopyLink} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-green-200 transition-colors hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow" data-id="element-96" data-mp="2vtnnjichf0x9">
                          <CopyIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-97" data-mp="lxfdd1919op0" />
                          {isLinkCopied ? 'Link copied' : 'Copy link'}
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div> : <form method="POST" action={WAITLIST_URL} target="waitlist_target" noValidate onSubmit={handleSubmit} className="max-w-lg mx-auto mb-6 space-y-4 text-left" data-id="element-98" data-mp="19qdwclqdoj2k">
                  <div data-id="element-99" data-mp="10p7ea882z077">
                    <label htmlFor="waitlist-name" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-100" data-mp="10nwcgdy742iq">
                      Full name
                    </label>
                    <input type="text" id="waitlist-name" name="name" autoComplete="name" value={name} onChange={e => setName(e.target.value)} className="w-full px-5 py-[14px] lg:py-4 min-h-[52px] rounded-full border border-white/20 bg-white/[0.08] focus:outline-none focus:ring-2 focus:ring-kitty-yellow focus:border-transparent text-white placeholder-gray-400 text-base" data-id="element-101" data-mp="3ledmw1shxm1p" />
                  </div>
                  <div data-id="element-102" data-mp="2eoiw8g2a0bmo">
                    <label htmlFor="waitlist-email" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-103" data-mp="2eosq4eqfz2xn">
                      Email address
                    </label>
                    <input type="email" id="waitlist-email" name="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-5 py-[14px] lg:py-4 min-h-[52px] rounded-full border border-white/20 bg-white/[0.08] focus:outline-none focus:ring-2 focus:ring-kitty-yellow focus:border-transparent text-white placeholder-gray-400 text-base" data-id="element-104" data-mp="2oldx35m0uo44" />
                  </div>

                  <p className="text-sm text-gray-400 leading-relaxed" data-id="element-105" data-mp="1382hyq2gk11e">
                    Join the KittyPot waitlist to hear when the app launches and
                    receive relevant KittyPot product updates.
                  </p>

                  <div className="flex items-start gap-3" data-id="element-106" data-mp="1w4ivr2ms8edq">
                    <input type="checkbox" id="waitlist-marketing" name="marketingOptIn" checked={marketingOptIn} onChange={e => setMarketingOptIn(e.target.checked)} className="mt-1 h-4 w-4 flex-shrink-0 rounded border-white/30 bg-white/[0.08] text-kitty-yellow focus:ring-2 focus:ring-kitty-yellow" data-id="element-107" data-mp="2132hb1xgro5k" />
                    <label htmlFor="waitlist-marketing" className="text-sm text-gray-400 leading-relaxed" data-id="element-108" data-mp="2f21w7hv6cooj">
                      Optional: also send me occasional KittyPot news and
                      offers. You can unsubscribe at any time using the link in
                      any email, or by emailing {SUPPORT_EMAIL}.
                    </label>
                  </div>

                  <button type="submit" disabled={status === 'loading'} className="w-full bg-kitty-yellow hover:bg-kitty-yellow-500 text-kitty-dark px-8 py-[16px] lg:py-4 min-h-[56px] rounded-full font-bold transition-colors duration-150 active:scale-[0.98] shadow-lg hover:shadow-xl flex items-center justify-center gap-2 text-base disabled:opacity-70 disabled:cursor-not-allowed mt-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-109" data-mp="2yqug20c66xf9">
                    {status === 'loading' ? <>
                        <LoaderIcon className="w-5 h-5 animate-spin" aria-hidden="true" data-id="element-110" data-mp="15urut856jq4w" />
                        <span data-id="element-111" data-mp="10vfdf2x2vb9p">Joining…</span>
                      </> : <>
                        Join the waitlist
                        <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-112" data-mp="w3nh0br8qbbn" />
                      </>}
                  </button>

                  <p className="text-xs text-gray-500" data-id="element-113" data-mp="r4szre8x8utn">
                    We use your details to contact you about the waitlist. See
                    our{' '}
                    <a href="/privacy-policy" className="font-semibold text-gray-300 underline underline-offset-2 transition-colors hover:text-white" data-id="element-114" data-mp="1jtrk8i0io924">
                      Privacy Policy
                    </a>
                    .
                  </p>

                  {status === 'error' && <p className="text-red-400 text-sm" role="alert" aria-live="polite" data-id="element-115" data-mp="34cbu60evsaak">
                      {errorMessage}
                    </p>}
                </form>}
            </>}
        </motion.div>
      </div>
    </section>;
}